import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo/light.png';
import '../styles/Landing.css';

const FEATURES = [
  {
    title: 'Order push on checkout',
    desc: 'The moment a customer confirms, your endpoint gets hit. No polling, no delay.',
  },
  {
    title: 'Two-way status sync',
    desc: 'Accept, mark ready, or complete from your POS. The customer app and driver update instantly.',
  },
  {
    title: 'Key-scoped per location',
    desc: 'Each API key covers one or more merchant tokens. Rotate a key without touching your webhook config.',
  },
  {
    title: 'Plain HTTP, no SDK',
    desc: 'POST requests with a JSON body. Works from any backend, any language.',
  },
];

const FLOW = [
  { dir: 'evt', label: 'Customer checks out on Ride Eats app',                         time: null,    ok: false },
  { dir: 'out', label: 'POST   /webhooks/your-pos   ·   event: order.new',             time: '11ms',  ok: true  },
  { dir: 'in',  label: 'HTTP 200 OK',                                                  time: null,    ok: true  },
  { dir: 'out', label: 'POST   /order/push   ·   { status: "accepted" }',              time: '340ms', ok: true  },
  { dir: 'out', label: 'POST   /order/push   ·   { status: "ready" }',                 time: '4m 12s',ok: true  },
  { dir: 'out', label: 'POST   /order/push   ·   { status: "completed" }',             time: '8m 54s',ok: true  },
  { dir: 'evt', label: 'Customer notified · driver dispatched',                        time: null,    ok: false },
];

export default function Landing() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="landing">

      {/* ── Nav ── */}
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <img src={logo} alt="Ride Eats" className="landing-logo-img" />
            <span className="landing-logo-name">Ride Eats <span className="landing-logo-sub">for Developers</span></span>
          </div>
          <nav className="landing-nav-links">
            <Link to="/docs" className="landing-nav-link">Docs</Link>
            {isAuthenticated
              ? <Link to="/dashboard" className="landing-nav-cta">Dashboard →</Link>
              : <Link to="/login"     className="landing-nav-cta">Sign in →</Link>
            }
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-hero-inner">

          <div className="landing-hero-left">
            <div className="landing-eyebrow">
              POS Integration API <span className="landing-eyebrow-v">v1</span>
            </div>
            <h1 className="landing-h1">
              Your kitchen,<br />
              <span className="landing-h1-accent">inside Ride Eats.</span>
            </h1>
            <p className="landing-hero-sub">
              One webhook per order, fired on checkout. One endpoint to push status back.
            </p>
            <div className="landing-hero-actions">
              <Link to="/docs" className="landing-btn-primary">Read the docs</Link>
              {isAuthenticated
                ? <Link to="/dashboard" className="landing-btn-ghost">Dashboard →</Link>
                : <Link to="/login"     className="landing-btn-ghost">Sign in →</Link>
              }
            </div>

          </div>

          <div className="landing-hero-right">
            <div className="landing-hero-code">
              <div className="lc-header">
                <span className="lc-dot" /><span className="lc-dot" /><span className="lc-dot" />
                <span className="lc-title">order.new · webhook payload</span>
              </div>
              <pre className="lc-pre"><code>{`POST https://your-pos.com/webhooks/ride-eats

{
  "event": "order.new",
  "data": {
    "merchant_token": "downtown",
    "order": {
      "id":      489734,
      "status":  "pending",
      "type":    "delivery",
      "payment": { "method": "cash", "amount": 600 },
      "items": [
        { "title": "Chicken Burger", "qty": 2, "total": 140 }
      ]
    }
  }
}`}</code></pre>
            </div>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className="landing-features">
        <div className="landing-section-inner">
          <div className="landing-section-label">What's included</div>
          <div className="landing-features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="landing-feature">
                <h3 className="landing-feature-title">{f.title}</h3>
                <p className="landing-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integration flow ── */}
      <section className="landing-flow">
        <div className="landing-section-inner">
          <div className="landing-flow-header">
            <div className="landing-section-label">Integration flow</div>
            <p className="landing-flow-sub">
              From customer checkout to your kitchen, and status updates back to the app.
            </p>
          </div>
          <div className="landing-flow-log">
            <div className="lf-bar">
              <span className="lf-dot" /><span className="lf-dot" /><span className="lf-dot" />
              <span className="lf-filename">ride-eats-integration.log</span>
            </div>
            <div className="lf-rows">
              {FLOW.map((row, i) => (
                <div key={i} className={`lf-row lf-row--${row.dir}`}>
                  <span className="lf-arrow">
                    {row.dir === 'out' ? '→' : row.dir === 'in' ? '←' : '·'}
                  </span>
                  <span className="lf-label">{row.label}</span>
                  <span className="lf-spacer" />
                  {row.time && <span className="lf-time">{row.time}</span>}
                  {row.ok   && <span className="lf-ok">200</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section className="landing-cta-band">
        <div className="landing-section-inner landing-cta-inner">
          <div>
            <div className="landing-cta-title">Connect your POS</div>
            <div className="landing-cta-sub">Read the API reference or sign in to manage keys and webhook endpoints.</div>
          </div>
          <div className="landing-cta-actions">
            <Link to="/docs" className="landing-btn-primary">View API reference</Link>
            {isAuthenticated
              ? <Link to="/dashboard" className="landing-btn-ghost">Dashboard →</Link>
              : <Link to="/login"     className="landing-btn-ghost">Sign in →</Link>
            }
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-section-inner landing-footer-inner">
          <span>© {new Date().getFullYear()} Ride Eats</span>
          <div className="landing-footer-links">
            <Link to="/docs">API Reference</Link>
            <Link to="/login">Developer Login</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
